import { AccountReceivableTransactionType, AccountingSubModules, AccountsReceivableEntities } from 'utils/enum/AccountingEnum';
import CatalogEnum from 'utils/enum/CatalogEnum';
import KeyStore from 'utils/KeyStore';
import Permission from 'utils/enum/Permissions';
import { parseInt, round } from 'lodash';

export default class AccountingUtils {
    static getTransactionType = (entity) => {
        switch (entity) {
        case AccountsReceivableEntities.OTHER_COLLECTIONS:
            return AccountReceivableTransactionType.OTHER;
        case AccountsReceivableEntities.SERVICE:
            return AccountReceivableTransactionType.SERVICE;
        case AccountsReceivableEntities.PETTY_CASH:
            return AccountReceivableTransactionType.PETTY_CASH;
        case AccountsReceivableEntities.PARTS:
            return AccountReceivableTransactionType.PARTS;
        default:
            return AccountReceivableTransactionType.DEALS;
        }
    };

    static getEntityByTransactionType = (type) => {
        switch (type) {
        case AccountReceivableTransactionType.OTHER:
            return AccountsReceivableEntities.OTHER_COLLECTIONS;
        case AccountReceivableTransactionType.SERVICE:
            return AccountsReceivableEntities.SERVICE;
        case AccountReceivableTransactionType.PETTY_CASH:
            return AccountsReceivableEntities.PETTY_CASH;
        case AccountReceivableTransactionType.PARTS:
            return AccountsReceivableEntities.PARTS;
        default:
            return AccountsReceivableEntities.DEALS;
        }
    };

    static getCatalogReason = (entity) => {
        switch (entity) {
        case AccountsReceivableEntities.SERVICE:
            return CatalogEnum.CASHIER_SERVICE_REASON;
        case AccountsReceivableEntities.OTHER_COLLECTIONS:
            return CatalogEnum.CASHIER_OTHER_REASON;
        case AccountsReceivableEntities.PETTY_CASH:
            return CatalogEnum.CASHIER_PETTY_CASH_REASON;
        case AccountsReceivableEntities.PARTS:
            return CatalogEnum.CASHIER_PARTS_REASON;
        default:
            return CatalogEnum.CASHIER_DEAL_REASON;
        }
    };

    static getIndexFromList = (list, selected) => {
        const tabIndex = list.indexOf(selected);
        if (tabIndex !== -1) return tabIndex;

        return 0;
    };

    static getSubModuleList = () => {
        const subModules = [];
        const keyStore = new KeyStore();

        const ACCOUNTING_BANKING_READ = keyStore.hasPermission(Permission.ACCOUNTING_BANKING_READ);
        const ACCOUNTING_GENERAL_JOURNAL_READ = keyStore.hasPermission(Permission.ACCOUNTING_GENERAL_JOURNAL_READ);
        const ACCOUNTING_ACCOUNT_RECEIVABLE_READ = keyStore.hasPermission(Permission.ACCOUNTING_ACCOUNT_RECEIVABLE_READ);
        const ACCOUNTING_ACCOUNTS_PAYABLE_READ = keyStore.hasPermission(Permission.ACCOUNTING_ACCOUNTS_PAYABLE_READ);

        if (ACCOUNTING_ACCOUNT_RECEIVABLE_READ) subModules.push(AccountingSubModules.ACCOUNTS_RECEIVABLE);
        if (ACCOUNTING_ACCOUNTS_PAYABLE_READ) subModules.push(AccountingSubModules.ACCOUNTS_PAYABLE);
        if (ACCOUNTING_BANKING_READ) subModules.push(AccountingSubModules.BANKING);
        if (ACCOUNTING_GENERAL_JOURNAL_READ) {
            subModules.push(
                AccountingSubModules.JOURNALS,
                AccountingSubModules.DEAL_JOURNALS,
                AccountingSubModules.INVENTORY_JOURNALS,
                AccountingSubModules.SERVICE_JOURNALS,
                AccountingSubModules.PARTS_TICKET_JOURNALS,
            );
        }

        return subModules;
    };

    static sumByKey(arr = [], key1, key2 = null, options = {}) {
        const { multiplyByQuantity = true, toFixed = 2, applyDiscount = false } = options;
        const total = arr.reduce((sum, item) => {
            const value1 = parseFloat(item[key1]) || 0;
            let value2 = 1;
            if (key2) {
                value2 = (parseFloat(item[key2]) || 0) / 100;
            }

            const quantity = multiplyByQuantity ? (parseInt(item.quantity) || 0) : 1;
            const discount = applyDiscount ? item.discount : 0;
            return sum + (((value1 * quantity) - discount) * value2);
        }, 0);
        return parseFloat(total.toFixed(toFixed));
    }

    static getSummaryLine(records) {
        const subTotalSummary = AccountingUtils.sumByKey(records, 'netPrice') || 0;
        const discountSummary = AccountingUtils.sumByKey(records, 'discount', '', { multiplyByQuantity: false }) || 0;
        const taxSummary = AccountingUtils.sumByKey(records, 'netPrice', 'surplusTax', {
            multiplyByQuantity: true,
            toFixed: 4,
            applyDiscount: true,
        }) || 0;

        const sumTotalSummary = round(subTotalSummary - discountSummary + taxSummary, 2) || 0;
        return {
            sumSubTotalReturn: subTotalSummary,
            sumDiscountReturn: discountSummary,
            sumTaxReturn: taxSummary,
            sumTotalReturn: sumTotalSummary,
        };
    }

    static calculateLineParts(record, discountPercent, isReturnPart) {
        const subTotal = Number(record.quantity * record.netPrice);
        const percent = isReturnPart ? record.discountPercent ?? 0 : discountPercent ?? 0;
        const discount = round((subTotal * percent) / 100, 2);
        const tax = round(round(round(subTotal, 2) - (discount ?? 0), 2) * ((record.surplusTax ?? 0) / 100), 4);
        const total = round((subTotal - discount) + tax, 2);

        return {
            subTotal,
            discount,
            tax,
            total,
        };
    }

    static getDefaultAccountsReceivableColumns() {
        return [
            {
                title: 'Account #',
                column: 'accountNumber',
                display: true,
            },
            {
                title: 'Remaining Due',
                column: 'remainingDue',
                additionalColumns: ['collectedAmount'],
                display: true,
            },
            {
                title: 'Balance Due',
                column: 'amountDue',
                display: true,
            },
            {
                title: 'Cash Down Payment Due',
                column: 'cashDownPayment',
                display: true,
            },
            {
                title: 'Cash Due at Signing',
                column: 'leaseTotalDueAtSigning',
                display: false,
            },
            {
                title: 'Reserve',
                column: 'reserveAmount',
                display: true,
            },
            {
                title: 'Date',
                column: 'soldDate',
                display: true,
            },
            {
                title: 'Buyer',
                column: 'buyer',
                display: true,
            },
            {
                title: 'Sales Rep',
                column: 'salesPerson1',
                display: true,
            },
            {
                title: 'Finance Manager',
                column: 'financeManager',
                display: true,
            },
            {
                title: 'Deal Type',
                column: 'dealType',
                display: true,
            },
            {
                title: 'Term',
                column: 'term',
                display: true,
            },
            {
                title: 'Payment',
                column: 'paymentAmount',
                display: true,
            },
            {
                title: 'First Payment Due Date',
                column: 'firstPaymentDue',
                display: true,
            },
            {
                title: 'Finance Company',
                column: 'financeCompany',
                display: true,
            },
            {
                title: 'Status',
                column: 'status',
                display: true,
            },
        ];
    }
}
